import React, { useEffect, useMemo, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import { escucharUsuariosAgenda, setAccesoAgenda, setPerfilUsuario, consultarContrasena } from '../objects/user';
import { PERFILES, perfilEfectivo, etiquetaPerfil } from '../utils/perfiles';

/**
 * Pestaña "Usuarios" — exclusiva del perfil Mesa de Ayuda.
 * Muestra los usuarios activos e inactivos de cada U.H. con acceso a la
 * Agenda Quirúrgica, permite crear nuevos, cambiar su perfil e
 * inactivarlos / reactivarlos.
 */
const UsersPanel = ({ uhList, onNuevoUsuario }) => {
  const [usuarios, setUsuarios] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error,    setError]    = useState('');
  const [unidad,   setUnidad]   = useState('todas');
  const [estado,   setEstado]   = useState('todos'); // todos | activos | inactivos
  const [registro, setRegistro] = useState('todos'); // todos | nuevos | anteriores
  const [busqueda, setBusqueda] = useState('');
  const [ocupado,  setOcupado]  = useState(null);    // id del usuario en actualización

  const miId = sessionStorage.getItem('id');

  useEffect(() => {
    const unsubscribe = escucharUsuariosAgenda(
      (lista) => { setUsuarios(lista); setCargando(false); },
      () => { setError('No se pudo cargar la lista de usuarios.'); setCargando(false); }
    );
    return () => unsubscribe && unsubscribe();
  }, []);

  const estaActivo = (u) => u.Activo && u.AccesoAgenda;

  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return usuarios
      .filter((u) => unidad === 'todas' || u.Unidad === unidad)
      .filter((u) => estado === 'todos' || (estado === 'activos' ? estaActivo(u) : !estaActivo(u)))
      .filter((u) => registro === 'todos' || (registro === 'nuevos' ? u.EsNuevo : !u.EsNuevo))
      .filter((u) => !q || u.Nombre.toLowerCase().includes(q) || u.Nick.toLowerCase().includes(q))
      .sort((a, b) => a.Unidad.localeCompare(b.Unidad) || a.Nombre.localeCompare(b.Nombre));
  }, [usuarios, unidad, estado, registro, busqueda]);

  const totalActivos = usuarios.filter(estaActivo).length;
  const totalNuevos  = usuarios.filter((u) => u.EsNuevo).length;

  const handleToggle = async (u) => {
    const activar = !estaActivo(u);
    const { isConfirmed } = await Swal.fire({
      icon: 'question',
      title: activar ? '¿Activar usuario?' : '¿Inactivar usuario?',
      text: `${u.Nombre} (${u.Nick}) · ${u.Unidad}`
        + (activar && !u.EsNuevo ? ' — Al activarlo pasará a la versión actual (dejará de mostrarse como "Anterior").' : ''),
      showCancelButton: true,
      confirmButtonText: activar ? 'Activar' : 'Inactivar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: activar ? '#04afaa' : '#e12f3b',
    });
    if (!isConfirmed) return;
    setOcupado(u.id);
    const res = await setAccesoAgenda(u, activar);
    setOcupado(null);
    if (res !== 'success') {
      Swal.fire({ icon: 'error', title: 'No se pudo actualizar', text: 'Intenta nuevamente.' });
    }
  };

  const handlePerfil = async (u, perfil) => {
    if (u.Permiso === perfil) return;
    setOcupado(u.id);
    const res = await setPerfilUsuario(u.id, perfil);
    setOcupado(null);
    if (res !== 'success') {
      Swal.fire({ icon: 'error', title: 'No se pudo cambiar el perfil', text: 'Intenta nuevamente.' });
    }
  };

  const unidades = [...new Set([
    ...(uhList || []).map((u) => u.hospital),
    ...usuarios.map((u) => u.Unidad).filter(Boolean),
  ])].sort((a, b) => a.localeCompare(b));

  return (
    <div>
      {/* Encabezado */}
      <div style={{ alignItems: 'flex-end', display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div>
          <h2 style={{ color: '#7c3aed', fontSize: '15px', fontWeight: 700, margin: '0 0 3px' }}>Usuarios</h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '12px', margin: 0 }}>
            {totalActivos} activos · {usuarios.length - totalActivos} inactivos · {usuarios.length} en total
            {' '}· {totalNuevos} nuevos · {usuarios.length - totalNuevos} de la versión anterior
          </p>
        </div>
        <button onClick={onNuevoUsuario} style={btnPrimario}>👤 Nuevo usuario</button>
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
        <select value={unidad} onChange={(e) => setUnidad(e.target.value)} style={inputStyle}>
          <option value="todas">Todas las unidades</option>
          {unidades.map((u) => <option key={u} value={u}>{u}</option>)}
        </select>
        <select value={estado} onChange={(e) => setEstado(e.target.value)} style={inputStyle}>
          <option value="todos">Activos e inactivos</option>
          <option value="activos">Solo activos</option>
          <option value="inactivos">Solo inactivos</option>
        </select>
        <select value={registro} onChange={(e) => setRegistro(e.target.value)} style={inputStyle}>
          <option value="todos">Nuevos y anteriores</option>
          <option value="nuevos">Solo nuevos</option>
          <option value="anteriores">Solo versión anterior</option>
        </select>
        <input
          type="search"
          placeholder="Buscar nombre o usuario…"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          style={{ ...inputStyle, minWidth: '220px' }}
        />
      </div>

      {cargando && <p style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Cargando usuarios…</p>}
      {error && <p style={{ color: '#e12f3b', fontSize: '13px', fontWeight: 600 }}>⚠ {error}</p>}

      {!cargando && !error && filtrados.length === 0 && (
        <p style={{ color: 'var(--text-tertiary)', fontSize: '13px', padding: '40px 0', textAlign: 'center' }}>
          No hay usuarios con esos filtros.
        </p>
      )}

      {!cargando && !error && filtrados.length > 0 && (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border-primary)', borderRadius: '12px', overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: '12px', minWidth: '1300px', width: '100%' }}>
            <thead>
              <tr style={{ background: 'var(--surface-sunken)', textAlign: 'left' }}>
                {['Nombre', 'Usuario', 'Contraseña', 'Unidad', 'Perfil', 'Estado', 'Último acceso', 'Creación', 'Última modificación', ''].map((h) => (
                  <th key={h} style={{ color: 'var(--text-secondary)', fontWeight: 700, padding: '10px 12px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtrados.map((u) => {
                const activo = estaActivo(u);
                const esYo   = u.id === miId;
                return (
                  <tr key={u.id} style={{ borderTop: '1px solid var(--border-secondary)', opacity: ocupado === u.id ? .5 : 1 }}>
                    <td style={td}>
                      {u.Nombre}{' '}
                      <span
                        title={u.EsNuevo ? 'Registrado con la versión actual' : 'Registrado en la versión anterior'}
                        style={{
                          background: u.EsNuevo ? '#f3ecfe' : 'var(--surface-sunken)', borderRadius: '20px',
                          color: u.EsNuevo ? '#7c3aed' : 'var(--text-secondary)', fontSize: '10px', fontWeight: 700,
                          marginLeft: '4px', padding: '1px 8px', whiteSpace: 'nowrap',
                        }}
                      >
                        {u.EsNuevo ? 'Nuevo' : 'Anterior'}
                      </span>
                    </td>
                    <td style={td}>{u.Nick}</td>
                    <td style={td}><Contrasena usuario={u} /></td>
                    <td style={td}>{u.Unidad || '—'}</td>
                    <td style={td}>
                      <select
                        value={perfilEfectivo(u.Permiso, u.Unidad === 'Corporativo')}
                        disabled={esYo || ocupado === u.id}
                        onChange={(e) => handlePerfil(u, e.target.value)}
                        title={esYo ? 'No puedes cambiar tu propio perfil' : `Nivel registrado: ${u.Permiso || '—'} (${etiquetaPerfil(u.Permiso, u.Unidad === 'Corporativo')})`}
                        style={{ ...inputStyle, padding: '4px 8px' }}
                      >
                        {PERFILES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                      </select>
                    </td>
                    <td style={td}>
                      <span style={{
                        background: activo ? '#e6f9f8' : '#fdf1f2', borderRadius: '20px',
                        color: activo ? '#038f8a' : '#991b1b', fontSize: '11px', fontWeight: 700, padding: '2px 10px',
                      }}>
                        {activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td style={td}><UltimoAcceso fecha={u.UltimoAcceso} total={u.TotalAccesos} /></td>
                    <td style={td}><Auditoria fecha={u.FechaCreacion} usuario={u.UsuarioCreacion} vacio={u.EsNuevo ? '—' : 'Versión anterior'} /></td>
                    <td style={td}><Auditoria fecha={u.FechaModificacion} usuario={u.UsuarioModificacion} vacio="Sin cambios" /></td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      {!esYo && (
                        <button
                          onClick={() => handleToggle(u)}
                          disabled={ocupado === u.id}
                          style={{
                            background: 'var(--surface)', border: `1px solid ${activo ? '#e12f3b' : '#04afaa'}`,
                            borderRadius: '8px', color: activo ? '#e12f3b' : '#039490', cursor: 'pointer',
                            fontSize: '11px', fontWeight: 700, padding: '4px 10px', whiteSpace: 'nowrap',
                          }}
                        >
                          {activo ? 'Inactivar' : 'Activar'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/**
 * Contraseña oculta por defecto. Se muestra solo al confirmar, se oculta
 * sola a los 10 segundos y cada consulta queda registrada en el usuario.
 */
const SEGUNDOS_VISIBLE = 10;
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const Contrasena = ({ usuario }) => {
  const [valor, setValor]       = useState(null);
  const [restante, setRestante] = useState(0);
  const [cargando, setCargando] = useState(false);
  const timer = useRef(null);

  useEffect(() => () => clearInterval(timer.current), []);

  const ocultar = () => { clearInterval(timer.current); setValor(null); setRestante(0); };

  const mostrar = async () => {
    const { isConfirmed } = await Swal.fire({
      icon: 'warning',
      title: '¿Mostrar contraseña?',
      html: `Se mostrará la contraseña de <b>${esc(usuario.Nombre)}</b> (${esc(usuario.Nick)}) durante ${SEGUNDOS_VISIBLE} segundos.<br/><br/>` +
            '<small>La consulta quedará registrada con tu nombre, fecha y hora.</small>',
      showCancelButton: true,
      confirmButtonText: 'Mostrar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#7c3aed',
    });
    if (!isConfirmed) return;
    setCargando(true);
    const pass = await consultarContrasena(usuario.id);
    setCargando(false);
    if (pass === null) {
      Swal.fire({ icon: 'error', title: 'No se pudo consultar la contraseña', text: 'Intenta nuevamente.' });
      return;
    }
    setValor(pass || '(vacía)');
    setRestante(SEGUNDOS_VISIBLE);
    clearInterval(timer.current);
    timer.current = setInterval(() => {
      setRestante((s) => { if (s <= 1) { ocultar(); return 0; } return s - 1; });
    }, 1000);
  };

  const ultima = usuario.UltimaConsultaPass
    ? `Última consulta: ${usuario.UltimaConsultaPass.toLocaleString('es-MX')} por ${usuario.UsuarioConsultaPass || '—'}`
    : 'Sin consultas registradas';

  return (
    <div style={{ alignItems: 'center', display: 'flex', gap: '6px' }} title={ultima}>
      <span style={{ fontFamily: valor ? 'monospace' : 'inherit', letterSpacing: valor ? 0 : '2px', userSelect: valor ? 'text' : 'none' }}>
        {valor ?? '••••••••'}
      </span>
      {valor ? (
        <button onClick={ocultar} style={btnIcono} title="Ocultar">🙈 {restante}s</button>
      ) : (
        <button onClick={mostrar} disabled={cargando} style={btnIcono} title="Mostrar contraseña">{cargando ? '…' : '👁'}</button>
      )}
    </div>
  );
};

const btnIcono = {
  background: 'var(--surface)', border: '1px solid var(--border-primary)', borderRadius: '6px',
  color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '11px', padding: '2px 6px', whiteSpace: 'nowrap',
};

/** Último acceso: fecha, hace cuánto y número de accesos registrados. */
const hace = (fecha) => {
  const min = Math.floor((Date.now() - fecha.getTime()) / 60000);
  if (min < 1)  return 'hace un momento';
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24)   return `hace ${h} h`;
  const d = Math.floor(h / 24);
  if (d < 30)   return `hace ${d} día${d === 1 ? '' : 's'}`;
  const m = Math.floor(d / 30);
  return m < 12 ? `hace ${m} mes${m === 1 ? '' : 'es'}` : `hace ${Math.floor(m / 12)} año(s)`;
};

const UltimoAcceso = ({ fecha, total }) => {
  if (!fecha) return <span style={{ color: 'var(--text-tertiary)' }}>Sin registro</span>;
  const dias = (Date.now() - fecha.getTime()) / 86400000;
  return (
    <div style={{ lineHeight: 1.35 }} title={`${total} acceso(s) registrados`}>
      <div>{fecha.toLocaleString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
      <div style={{ color: dias > 30 ? '#b45309' : 'var(--text-secondary)', fontSize: '11px' }}>
        {hace(fecha)} · {total} acceso{total === 1 ? '' : 's'}
      </div>
    </div>
  );
};

/** Fecha + usuario de un evento de auditoría. */
const Auditoria = ({ fecha, usuario, vacio }) => {
  if (!fecha && !usuario) return <span style={{ color: 'var(--text-tertiary)' }}>{vacio}</span>;
  return (
    <div style={{ lineHeight: 1.35 }}>
      <div>{fecha ? fecha.toLocaleString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</div>
      <div style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>{usuario || '—'}</div>
    </div>
  );
};

const td = { color: 'var(--text-primary)', padding: '8px 12px', verticalAlign: 'middle' };

const inputStyle = {
  background: 'var(--surface)', border: '1px solid var(--border-primary)', borderRadius: '8px',
  color: 'var(--text-primary)', fontSize: '12px', padding: '6px 10px',
};

const btnPrimario = {
  alignItems: 'center', background: '#f3ecfe', border: '1px solid #7c3aed', borderRadius: '8px',
  color: '#7c3aed', cursor: 'pointer', display: 'flex', fontSize: '12px', fontWeight: 600,
  gap: '5px', padding: '6px 12px', whiteSpace: 'nowrap',
};

export default UsersPanel;
